import { parseOrderIdParam } from './paymentSubject';

// Route switch for /external/payment/:lang/:orderId (pure, unit-tested).
// Full cutover (2026-10-14): every link the payment parser understands opens the
// EveryPay page (ws5 pay/*): ?subject=package|order|completed|tip, a bare order rec
// id (defaulted to `order`, retried once as `completed` by the page) and the legacy
// "<recid>tips" / "<recid>completed" suffixes in links already sent to customers.
// The decision uses the SAME parser the EveryPay page runs, so the two cannot
// disagree. An id the parser rejects still renders the legacy page, which shows its
// own error screen.
export const EVERYPAY_SUBJECTS = Object.freeze(['package', 'order', 'completed', 'tip']);

export function isEveryPayLink(orderId, searchParams) {
    const parsed = parseOrderIdParam(orderId, searchParams);
    return Boolean(parsed && EVERYPAY_SUBJECTS.includes(parsed.subject));
}

// Subjects the legacy Montonio page (Payment.jsx -> ws1 create_payment_link_new)
// can still take over. A package has no legacy route on that page.
const LEGACY_FALLBACK_SUBJECTS = Object.freeze(['order', 'completed', 'tip']);

/**
 * True when an EveryPay refusal should hand the link to the legacy Montonio page
 * instead of showing "payment not available". ws5 answers `not_available` when the
 * web rail is switched off (env pay_link_enabled != "1", the emergency brake) and
 * for useTmpPrice orders, which the new rail does not price. Both keep working
 * through Montonio until its removal (plan Phase 5).
 */
export function shouldFallBackToLegacy(refuseReason, subject) {
    return String(refuseReason || '').trim().toLowerCase() === 'not_available'
        && LEGACY_FALLBACK_SUBJECTS.includes(subject);
}

// The legacy page knows only the ws1 suffix convention: it reads the :orderId
// segment and nothing else (no ?subject=), and ws1 create_payment_link_new picks the
// table and the amount from the suffix. A fallback must therefore hand it the
// suffixed id, or a tip or completed-order link would be charged as an order.
const LEGACY_ID_SUFFIX = Object.freeze({ order: '', completed: 'completed', tip: 'tips' });

/** The :orderId the legacy page needs for this subject + rec id ('' when it has none). */
export function legacyOrderId(subject, recid) {
    const id = typeof recid === 'string' ? recid.trim() : '';
    if (!id || !Object.prototype.hasOwnProperty.call(LEGACY_ID_SUFFIX, subject)) return '';
    return `${id}${LEGACY_ID_SUFFIX[subject]}`;
}
